import { StateCreator } from 'zustand';
import { getAgencyClinics } from '../../services/clinicsApi';
import type {
  AgencyClinicResponse,
  KinnserClinicDataResponse,
  KinnserPersonInfo,
} from '../../services/api/types.gen';
import { client } from '../../services/api';

// Type for clinician objects in clinic data
interface ClinicianInfo {
  id: string;
  name: string;
  [key: string]: unknown;
}

// Cache entry for Kinnser clinic data
interface KinnserClinicDataCacheEntry {
  data: KinnserClinicDataResponse;
  lastFetched: number;
}

// Helper to build clinicians map from agency clinics data
const buildCliniciansMap = (
  clinics: AgencyClinicResponse[]
): Record<string, string> => {
  const map: Record<string, string> = {};
  for (const clinic of clinics) {
    const clinicians = clinic.clinicians as ClinicianInfo[] | null | undefined;
    if (clinicians) {
      for (const clinician of clinicians) {
        if (clinician.id && typeof clinician.name === 'string') {
          map[clinician.id] = clinician.name;
        }
      }
    }
  }
  return map;
};

// Cache TTL for Kinnser clinic data (5 minutes)
const KINNSER_CLINIC_DATA_CACHE_TTL = 5 * 60 * 1000;

export interface AgencyClinicSlice {
  // Agency clinics (list of clinics)
  agencyClinics: AgencyClinicResponse[];
  agencyClinicsLoading: boolean;
  agencyClinicsError: string | null;
  agencyClinicsLastFetched: number | null;
  cliniciansMap: Record<string, string>;
  fetchAgencyClinics: (agencyId?: string) => Promise<void>;
  clearAgencyClinics: () => void;
  getAgencyClinicById: (id: string) => AgencyClinicResponse | undefined;
  getAgencyClinicByName: (name: string) => AgencyClinicResponse | undefined;
  getClinicianNameById: (id: string) => string | undefined;

  // Kinnser clinic data (detailed clinic info including physicians, clinicians, etc.)
  kinnserClinicDataCache: Record<string, KinnserClinicDataCacheEntry>;
  kinnserClinicDataLoading: Record<string, boolean>;
  kinnserClinics: KinnserPersonInfo[];
  kinnserClinicsLoading: boolean;
  fetchKinnserClinics: (
    agencySystemName: string
  ) => Promise<KinnserPersonInfo[]>;
  fetchKinnserClinicData: (
    agencySystemName: string,
    clinicId: string
  ) => Promise<KinnserClinicDataResponse | null>;
  getKinnserClinicData: (clinicId: string) => KinnserClinicDataResponse | null;
  isKinnserClinicDataLoading: (clinicId: string) => boolean;
  clearKinnserClinicDataCache: () => void;
}

export const agencyClinicSlice: StateCreator<AgencyClinicSlice> = (
  set,
  get
) => ({
  // Agency clinics state
  agencyClinics: [],
  agencyClinicsLoading: false,
  agencyClinicsError: null,
  agencyClinicsLastFetched: null,
  cliniciansMap: {},

  // Kinnser clinic data state
  kinnserClinicDataCache: {},
  kinnserClinicDataLoading: {},
  kinnserClinics: [],
  kinnserClinicsLoading: false,

  fetchAgencyClinics: async (agencyId?: string) => {
    const state = get();
    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000;

    // Check if we have recent data for the same agency
    const hasRecentData =
      state.agencyClinicsLastFetched &&
      now - state.agencyClinicsLastFetched < fiveMinutes;

    if (hasRecentData && state.agencyClinics.length > 0) {
      return;
    }

    try {
      set({ agencyClinicsLoading: true, agencyClinicsError: null });
      const agencyClinicsData = await getAgencyClinics(agencyId);

      if (agencyClinicsData) {
        set({
          agencyClinics: agencyClinicsData,
          agencyClinicsLoading: false,
          agencyClinicsLastFetched: now,
          cliniciansMap: buildCliniciansMap(agencyClinicsData),
        });
      } else {
        set({
          agencyClinics: [],
          agencyClinicsLoading: false,
          agencyClinicsError: 'No data received',
          agencyClinicsLastFetched: now,
          cliniciansMap: {},
        });
      }
    } catch (error) {
      console.error('Error fetching agency clinics:', error);
      set({
        agencyClinics: [],
        agencyClinicsLoading: false,
        agencyClinicsError:
          error instanceof Error ? error.message : 'Unknown error',
        agencyClinicsLastFetched: now,
        cliniciansMap: {},
      });
    }
  },

  clearAgencyClinics: () => {
    set({
      agencyClinics: [],
      agencyClinicsLoading: false,
      agencyClinicsError: null,
      agencyClinicsLastFetched: null,
      cliniciansMap: {},
    });
  },

  getAgencyClinicById: (id: string) => {
    return get().agencyClinics.find(clinic => clinic.id === id);
  },

  getAgencyClinicByName: (name: string) => {
    return get().agencyClinics.find(clinic => clinic.name === name);
  },

  getClinicianNameById: (id: string) => {
    return get().cliniciansMap[id];
  },

  // Fetch list of Kinnser clinics for an agency
  fetchKinnserClinics: async (agencySystemName: string) => {
    const state = get();

    // Return cached data if available
    if (state.kinnserClinics.length > 0) {
      return state.kinnserClinics;
    }

    // Prevent duplicate fetches
    if (state.kinnserClinicsLoading) {
      // Wait for existing fetch to complete
      return new Promise<KinnserPersonInfo[]>(resolve => {
        const checkInterval = setInterval(() => {
          const currentState = get();
          if (!currentState.kinnserClinicsLoading) {
            clearInterval(checkInterval);
            resolve(currentState.kinnserClinics);
          }
        }, 100);
      });
    }

    try {
      set({ kinnserClinicsLoading: true });

      const response = await client.request({
        method: 'GET',
        url: `/rogerdoc/api/clinics/${agencySystemName}`,
      });

      const clinicsData = response.data as { name: string; id: string }[];
      if (clinicsData) {
        const mappedClinics = clinicsData.map(clinic => ({
          name: `${clinic.name} - ${clinic.id}`,
          id: clinic.id,
        }));

        set({
          kinnserClinics: mappedClinics,
          kinnserClinicsLoading: false,
        });

        return mappedClinics;
      }

      set({ kinnserClinicsLoading: false });
      return [];
    } catch (error) {
      console.error('Error fetching Kinnser clinics:', error);
      set({ kinnserClinicsLoading: false });
      return [];
    }
  },

  // Fetch detailed Kinnser clinic data (physicians, clinicians, etc.)
  fetchKinnserClinicData: async (
    agencySystemName: string,
    clinicId: string
  ) => {
    const state = get();
    const now = Date.now();
    const cacheKey = `${agencySystemName}:${clinicId}`;

    // Check if we have valid cached data
    const cached = state.kinnserClinicDataCache[cacheKey];
    if (cached && now - cached.lastFetched < KINNSER_CLINIC_DATA_CACHE_TTL) {
      return cached.data;
    }

    // Prevent duplicate fetches for the same clinic
    if (state.kinnserClinicDataLoading[cacheKey]) {
      // Wait for existing fetch to complete
      return new Promise<KinnserClinicDataResponse | null>(resolve => {
        const checkInterval = setInterval(() => {
          const currentState = get();
          if (!currentState.kinnserClinicDataLoading[cacheKey]) {
            clearInterval(checkInterval);
            const cachedData = currentState.kinnserClinicDataCache[cacheKey];
            resolve(cachedData?.data || null);
          }
        }, 100);
      });
    }

    try {
      set(state => ({
        kinnserClinicDataLoading: {
          ...state.kinnserClinicDataLoading,
          [cacheKey]: true,
        },
      }));

      const response = await client.request({
        method: 'GET',
        url: `/rogerdoc/api/clinics/info/${agencySystemName}`,
        query: { clinic_id: clinicId },
      });

      // Roger Doc returns an array, take the first element
      const responseArray = response.data as Array<{
        agency_id?: string;
        clinic_id?: string;
        clinic_name?: string;
        auto_mrn?: boolean;
        physicians?: Array<{ name: string; id: string }>;
        clinicians?: Array<{ name: string; id: string }>;
        clinical_care_managers?: Array<{ name: string; id: string }>;
        case_managers?: Array<{ name: string; id: string }>;
        oasis_approvers?: Array<{ name: string; id: string }>;
        pt_approvers?: Array<{ name: string; id: string }>;
        ot_approvers?: Array<{ name: string; id: string }>;
        st_approvers?: Array<{ name: string; id: string }>;
        primary_schedulers?: Array<{ name: string; id: string }>;
        secondary_schedulers?: Array<{ name: string; id: string }>;
        branches?: Array<{ name: string; id: string }>;
        insurance_providers?: Array<{
          name: string;
          id: string;
          type: number;
        }>;
      }>;

      const rawData = responseArray?.[0];
      if (rawData) {
        const mappedData: KinnserClinicDataResponse = {
          auto_mrn: rawData.auto_mrn ?? false,
          clinic_key: rawData.clinic_id ?? clinicId,
          clinic_name: rawData.clinic_name ?? '',
          physicians: rawData.physicians,
          clinicians: rawData.clinicians,
          clinical_care_managers: rawData.clinical_care_managers,
          case_managers: rawData.case_managers,
          oasis_approvers: rawData.oasis_approvers,
          pt_approvers: rawData.pt_approvers,
          ot_approvers: rawData.ot_approvers,
          st_approvers: rawData.st_approvers,
          primary_schedulers: rawData.primary_schedulers,
          secondary_schedulers: rawData.secondary_schedulers,
          branches: rawData.branches,
          insurance_providers: rawData.insurance_providers,
        };

        set(state => ({
          kinnserClinicDataCache: {
            ...state.kinnserClinicDataCache,
            [cacheKey]: {
              data: mappedData,
              lastFetched: now,
            },
          },
          kinnserClinicDataLoading: {
            ...state.kinnserClinicDataLoading,
            [cacheKey]: false,
          },
        }));

        return mappedData;
      }

      set(state => ({
        kinnserClinicDataLoading: {
          ...state.kinnserClinicDataLoading,
          [cacheKey]: false,
        },
      }));
      return null;
    } catch (error) {
      console.error('Error fetching Kinnser clinic data:', error);
      set(state => ({
        kinnserClinicDataLoading: {
          ...state.kinnserClinicDataLoading,
          [cacheKey]: false,
        },
      }));
      return null;
    }
  },

  // Get cached Kinnser clinic data (synchronous)
  getKinnserClinicData: (clinicId: string) => {
    const state = get();
    // Find the clinic data in cache by clinic ID (any agency)
    for (const [key, entry] of Object.entries(state.kinnserClinicDataCache)) {
      if (key.endsWith(`:${clinicId}`)) {
        return entry.data;
      }
    }
    return null;
  },

  // Check if Kinnser clinic data is loading
  isKinnserClinicDataLoading: (clinicId: string) => {
    const state = get();
    for (const key of Object.keys(state.kinnserClinicDataLoading)) {
      if (key.endsWith(`:${clinicId}`) && state.kinnserClinicDataLoading[key]) {
        return true;
      }
    }
    return false;
  },

  // Clear Kinnser clinic data cache
  clearKinnserClinicDataCache: () => {
    set({
      kinnserClinicDataCache: {},
      kinnserClinicDataLoading: {},
      kinnserClinics: [],
      kinnserClinicsLoading: false,
    });
  },
});
